import { ButtonIcon, ButtonSize, ButtonVariant, IconVariants, Image, Modal, ModalSize, Shape, Text } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import React, { type MouseEvent, type TouchEvent, useEffect } from "react";

import { type AvatarGeneratedImage } from "@/components/AvatarGeneratorModal/types";
import { DOM_EVENTS, KEYBOARD_KEYS } from "@/constants/dom";

interface IEnlargedImageModalProps {
  image: AvatarGeneratedImage | null;
  imageIndex: number | null;
  totalImages: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onDownload: () => void;
  onTouchStart: (e: TouchEvent) => void;
  onTouchMove: (e: TouchEvent) => void;
  onTouchEnd: () => void;
}

export const EnlargedImageModal = ({
  image,
  imageIndex,
  totalImages,
  onClose,
  onPrev,
  onNext,
  onDownload,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}: IEnlargedImageModalProps) => {
  const { t } = useTranslation();
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (imageIndex !== null) {
        if (e.key === KEYBOARD_KEYS.ARROW_LEFT) {
          e.preventDefault();
          onPrev();
        } else if (e.key === KEYBOARD_KEYS.ARROW_RIGHT) {
          e.preventDefault();
          onNext();
        }
      }
    };

    window.addEventListener(DOM_EVENTS.KEYDOWN, handleKeyDown);
    return () => window.removeEventListener(DOM_EVENTS.KEYDOWN, handleKeyDown);
  }, [imageIndex, onPrev, onNext]);

  if (!image || imageIndex === null) {
    return null;
  }

  return (
    <Modal size={ModalSize.FULLSCREEN} open={true} onOpenChange={onClose}>
      <div
        className="relative w-full h-full flex items-center justify-center"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {imageIndex > 0 && (
          <ButtonIcon
            icon={IconVariants.CHEVRON_LEFT}
            size={ButtonSize.LARGE}
            shape={Shape.SQUARE}
            variant={ButtonVariant.CONTAINED}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 z-10"
            aria-label={t("common:modal.generateAvatar.ariaLabel.previousImage")}
          />
        )}

        {imageIndex < totalImages - 1 && (
          <ButtonIcon
            icon={IconVariants.CHEVRON_RIGHT}
            size={ButtonSize.LARGE}
            shape={Shape.SQUARE}
            variant={ButtonVariant.CONTAINED}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 z-10"
            aria-label={t("common:modal.generateAvatar.ariaLabel.nextImage")}
          />
        )}

        {totalImages > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 px-4 py-2 rounded-full z-10">
            <Text className="text-white text-sm">
              {imageIndex + 1} / {totalImages}
            </Text>
          </div>
        )}

        <ButtonIcon
          icon={IconVariants.DOWNLOAD}
          size={ButtonSize.LARGE}
          shape={Shape.SQUARE}
          variant={ButtonVariant.CONTAINED}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onDownload();
          }}
          className="absolute bottom-6 right-6 bg-black/60 hover:bg-black/80 z-10"
          aria-label={t("common:modal.generateAvatar.ariaLabel.downloadImage")}
        />

        <div className="flex items-center justify-center max-w-[90vw] max-h-[90vh]" onClick={(e: MouseEvent) => e.stopPropagation()}>
          <Image
            src={`data:${image.type};base64,${image.base64}`}
            alt={t("common:modal.generateAvatar.imageAlt.generatedImage", { index: imageIndex + 1 })}
            className="max-w-full max-h-[90vh] object-contain"
          />
        </div>
      </div>
    </Modal>
  );
};
